/**
 * useApi Hooks
 *
 * Custom hooks that wrap TanStack React Query's useQuery and useMutation
 * with integrated toast notifications for error and success feedback.
 *
 * These hooks provide a consistent API interaction pattern across the app.
 *
 * @example
 * ```ts
 * // Query example
 * const { data, isLoading, error } = useApiQuery({
 *   queryKey: ['users', 'me'],
 *   queryFn: () => userService.getMe(),
 *   successMessage: undefined, // no toast on success for queries
 * });
 *
 * // Mutation example
 * const { mutate, isPending } = useApiMutation({
 *   mutationFn: (data) => userService.updateProfile(data),
 *   successMessage: 'Profile updated successfully!',
 *   errorMessage: 'Failed to update profile.',
 *   onSuccess: (data) => { ... },
 * });
 * ```
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
  type UseMutationOptions,
  type QueryKey,
  type InvalidateQueryFilters,
} from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { ApiError } from '../services/api/errors';
import { useToast } from './useToast';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface UseApiQueryOptions<TData = unknown, TError = ApiError>
  extends Omit<UseQueryOptions<TData, TError>, 'queryKey' | 'queryFn'> {
  /** React Query cache key */
  queryKey: QueryKey;
  /** Function that returns a promise of the data */
  queryFn: () => Promise<TData>;
  /** Optional success toast message (rarely used for queries) */
  successMessage?: string;
  /** Optional custom error message (overrides API error message) */
  errorMessage?: string;
  /** Whether to show error toasts (default: true) */
  showErrorToast?: boolean;
}

export interface UseApiMutationOptions<
  TData = unknown,
  TVariables = void,
  TError = ApiError,
  TContext = unknown,
> extends Omit<UseMutationOptions<TData, TError, TVariables, TContext>, 'mutationFn'> {
  /** Function that performs the mutation */
  mutationFn: (variables: TVariables) => Promise<TData>;
  /** Toast message on success */
  successMessage?: string;
  /** Toast title on success (default: 'Success') */
  successTitle?: string;
  /** Custom error message (overrides API error message) */
  errorMessage?: string;
  /** Toast title on error (default: 'Error') */
  errorTitle?: string;
  /** Whether to show success toasts (default: true if successMessage provided) */
  showSuccessToast?: boolean;
  /** Whether to show error toasts (default: true) */
  showErrorToast?: boolean;
  /** Query keys to invalidate on success */
  invalidateKeys?: QueryKey[];
}

// ─── useDebounce ────────────────────────────────────────────────────────────

/**
 * Debounces a value by the specified delay.
 *
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds (default: 300)
 * @returns The debounced value
 *
 * @example
 * ```ts
 * const [search, setSearch] = useState('');
 * const debouncedSearch = useDebounce(search, 300);
 * ```
 */
export function useDebounce<T>(value: T, delay: number = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

// ─── useApiQuery ────────────────────────────────────────────────────────────

/**
 * Custom query hook with toast integration.
 *
 * Wraps useQuery with automatic error toast notifications.
 * Success toasts can be enabled optionally (not typical for queries).
 */
export function useApiQuery<TData = unknown, TError = ApiError>({
  queryKey,
  queryFn,
  successMessage,
  errorMessage,
  showErrorToast = true,
  ...options
}: UseApiQueryOptions<TData, TError>) {
  const toastHook = useToast();

  return useQuery<TData, TError>({
    queryKey,
    queryFn: async () => {
      try {
        const data = await queryFn();

        if (successMessage) {
          toastHook.success('Success', successMessage);
        }

        return data;
      } catch (error) {
        if (showErrorToast) {
          const message =
            errorMessage ||
            (error instanceof ApiError ? error.message : 'An unexpected error occurred.');
          toastHook.error('Error', message);
        }
        throw error;
      }
    },
    ...options,
  });
}

// ─── useApiMutation ─────────────────────────────────────────────────────────

/**
 * Custom mutation hook with toast integration and cache invalidation.
 *
 * Wraps useMutation with automatic success and error toast notifications
 * and optional query cache invalidation on success.
 */
export function useApiMutation<
  TData = unknown,
  TVariables = void,
  TError = ApiError,
  TContext = unknown,
>({
  mutationFn,
  successMessage,
  successTitle = 'Success',
  errorMessage,
  errorTitle = 'Error',
  showSuccessToast,
  showErrorToast = true,
  invalidateKeys,
  onSuccess,
  onError,
  ...options
}: UseApiMutationOptions<TData, TVariables, TError, TContext>) {
  const toastHook = useToast();
  const queryClientInstance = useQueryClient();

  // Default: show success toast only if successMessage is provided
  const shouldShowSuccess = showSuccessToast ?? !!successMessage;

  return useMutation<TData, TError, TVariables, TContext>({
    mutationFn,
    onSuccess: async (data, variables, context, ...rest) => {
      // Show success toast
      if (shouldShowSuccess && successMessage) {
        toastHook.success(successTitle, successMessage);
      }

      // Invalidate specified query keys
      if (invalidateKeys && invalidateKeys.length > 0) {
        await Promise.all(
          invalidateKeys.map((key) =>
            queryClientInstance.invalidateQueries({ queryKey: key } as InvalidateQueryFilters)
          )
        );
      }

      // Call user-provided onSuccess
      onSuccess?.(data, variables, context, ...rest);
    },
    onError: (error, variables, context, ...rest) => {
      if (showErrorToast) {
        const message =
          errorMessage ||
          (error instanceof ApiError ? error.message : 'An unexpected error occurred.');
        toastHook.error(errorTitle, message);
      }

      // Call user-provided onError
      onError?.(error, variables, context, ...rest);
    },
    ...options,
  });
}

// ─── useInvalidateQueries ───────────────────────────────────────────────────

/**
 * Hook that returns a function to invalidate query cache by keys.
 * Useful for manual cache invalidation from event handlers.
 *
 * @example
 * ```ts
 * const invalidate = useInvalidateQueries();
 * await invalidate(['workspaces']);
 * ```
 */
export function useInvalidateQueries() {
  const queryClientInstance = useQueryClient();

  return async (queryKey: QueryKey) => {
    await queryClientInstance.invalidateQueries({ queryKey } as InvalidateQueryFilters);
  };
}